import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Typography, Select, Tabs } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const RegisterPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [registerType, setRegisterType] = useState<'phone' | 'email'>('phone');
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // 发送验证码
  const handleSendCode = async () => {
    try {
      const target = registerType === 'phone' 
        ? form.getFieldValue('phone') 
        : form.getFieldValue('email');
        
      if (!target) {
        message.warning(`请先输入${registerType === 'phone' ? '手机号' : '邮箱'}`);
        return;
      }

      setSending(true);
      await api.post('/send-code', { target, type: registerType });
      message.success('验证码已发送');
      
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) { clearInterval(timer); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      message.error(error.response?.data?.detail || '发送失败');
    } finally {
      setSending(false);
    }
  };

  // 提交注册
  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const payload = {
        username: values.username,
        password: values.password,
        phone: registerType === 'phone' ? values.phone : undefined,
        email: registerType === 'email' ? values.email : undefined,
        code: values.code,
        code_type: registerType,
        role: values.role,
        company_size: values.company_size,
        practice_area: values.practice_area || [],
      };
      
      await api.post('/register', payload);
      message.success('注册成功，请登录');
      navigate('/login');
    } catch (error: any) {
      message.error(error.response?.data?.detail || '注册失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f0f2f5', padding: '20px 0' }}>
      <Card style={{ width: 450, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <Title level={2} style={{ textAlign: 'center', marginBottom: 24 }}>注册账号</Title>
        
        <Tabs 
          activeKey={registerType} 
          onChange={(key) => setRegisterType(key as 'phone' | 'email')} 
          centered
          style={{ marginBottom: 24 }}
        >
          <TabPane tab="手机号注册" key="phone" />
          <TabPane tab="邮箱注册" key="email" />
        </Tabs>

        <Form form={form} onFinish={onFinish} layout="vertical" size="large">
          <Form.Item name="username" rules={[{ required: true, message: '请输入用户名' }]}>
            <Input prefix={<UserOutlined />} placeholder="用户名" />
          </Form.Item>
          
          <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="密码" />
          </Form.Item>

          {/* 动态切换：手机号 或 邮箱 */}
          {registerType === 'phone' ? (
            <Form.Item name="phone" rules={[{ required: true, message: '请输入手机号' }]}>
              <Input prefix={<PhoneOutlined />} placeholder="手机号" />
            </Form.Item>
          ) : (
            <Form.Item name="email" rules={[{ required: true, message: '请输入邮箱' }, { type: 'email', message: '邮箱格式不正确' }]}>
              <Input prefix={<MailOutlined />} placeholder="邮箱" />
            </Form.Item>
          )}

          {/* 验证码 */}
          <Form.Item name="code" rules={[{ required: true, message: '请输入验证码' }]}>
            <Input
              placeholder="验证码"
              suffix={
                <Button
                  type="link"
                  onClick={handleSendCode}
                  disabled={countdown > 0 || sending}
                  style={{ padding: 0 }}
                >
                  {countdown > 0 ? `${countdown}s` : '获取验证码'}
                </Button>
              }
            />
          </Form.Item>

          <Form.Item name="role" label="您的身份" rules={[{ required: true, message: '请选择您的身份' }]}>
            <Select placeholder="请选择您的身份">
              <Select.Option value="lawyer">律师</Select.Option>
              <Select.Option value="legal">法务</Select.Option>
              <Select.Option value="owner">企业主</Select.Option>
              <Select.Option value="other">其他</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="company_size" label="公司/律所规模">
            <Select placeholder="请选择规模">
              <Select.Option value="1-10">1-10 人</Select.Option>
              <Select.Option value="11-50">11-50 人</Select.Option>
              <Select.Option value="51-200">51-200 人</Select.Option>
              <Select.Option value="200+">200 人以上</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="practice_area" label="主要业务领域">
            <Select mode="multiple" placeholder="请选择（可多选）">
              <Select.Option value="contract">合同审核</Select.Option>
              <Select.Option value="labor">劳动纠纷</Select.Option>
              <Select.Option value="ip">知识产权</Select.Option>
              <Select.Option value="corporate">公司治理</Select.Option>
              <Select.Option value="other">其他</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              注册
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center' }}>
          <Text>已有账号？</Text>
          <Link to="/login">立即登录</Link>
        </div>
      </Card>
    </div>
  );
};

export default RegisterPage;